import { ref, watch, type Ref } from 'vue';
import { campaignsClient } from '~/services';
import { useMessageRules } from '~/components/quick-blast/useMessageRules';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { useToasted } from '~/composables/useToasted';
import { catchRequestError } from '~/utils/request';
import { buildCampaignMessage, type MessageStandardsError } from '~/constants/campaign';

interface FieldBinding {
    key: string;
    value: string | null;
}

interface UseValidateMessageStandardsOptions {
    campaignType: () => 'marketing' | 'transactional';
    messageBody: () => string;
    messageMediaUrl: () => string | null | undefined;
    messageMediaContentType: () => string | null | undefined;
    messageSnippetId: () => number | null | undefined;
    messageSnippetFieldBindings: () => FieldBinding[];
    snippetIsLocked: () => boolean;
    audienceSegmentIds: () => number[];
    audienceExternalContactListIds: () => number[];
    audienceGroupIds: () => string[];
    validationContext?: {
        orgUnitId: () => number | undefined;
        aiVettingEnabled: () => boolean;
    };
}

interface UseValidateMessageStandardsReturn {
    bodyErrors: Ref<MessageStandardsError[]>;
    validate: () => Promise<boolean>;
    clearErrors: () => void;
}

export const useValidateMessageStandards = (
    options: UseValidateMessageStandardsOptions
): UseValidateMessageStandardsReturn => {
    const teamId = Spark.state.currentTeam.id;
    const { isAiVettingEnabled } = useMessageRules();
    const { currentOrgUnit, currentGroup } = useCurrentGroup();
    const toasted = useToasted();

    const bodyErrors = ref<MessageStandardsError[]>([]);

    watch([() => options.snippetIsLocked()], () => {
        clearErrors();
    });

    const requestValidateMessageStandards = async () => {
        const audience: Record<string, string | number[] | string[] | undefined> = {
            type: options.campaignType(),
            segment_ids: options.audienceSegmentIds(),
            external_contact_list_ids: options.audienceExternalContactListIds(),
        };

        let orgUnitId: number | undefined;
        let groupId: string | undefined;

        if (options.validationContext) {
            orgUnitId = options.validationContext.orgUnitId();
        } else {
            orgUnitId = currentOrgUnit.value?.id;
            groupId = currentGroup.value?.group_id;
        }

        if (orgUnitId) {
            audience.group_ids = options.audienceGroupIds();
        }

        const payload = {
            audience,
            campaign_message: buildCampaignMessage({
                snippetId: options.messageSnippetId(),
                snippetIsLocked: options.snippetIsLocked(),
                fieldBindings: options.messageSnippetFieldBindings() || [],
                body: options.messageBody(),
                mediaUrl: options.messageMediaUrl(),
                mediaContentType: options.messageMediaContentType(),
            }),
        };

        if (orgUnitId) {
            return campaignsClient.orgUnitValidateMessageStandards(teamId, orgUnitId, payload);
        } else if (groupId) {
            return campaignsClient.groupValidateMessageStandards(teamId, groupId, payload);
        }
    };

    const validate = async (): Promise<boolean> => {
        if (options.snippetIsLocked()) {
            clearErrors();
            return true;
        }

        const aiEnabled = options.validationContext
            ? options.validationContext.aiVettingEnabled()
            : isAiVettingEnabled(options.campaignType());

        if (!aiEnabled) {
            clearErrors();
            return true;
        }

        try {
            const response = await requestValidateMessageStandards();

            if (!response) {
                return false;
            }

            const errors: MessageStandardsError[] = response.data.errors || [];

            bodyErrors.value = errors;
            return !errors.some((e) => e.severity === 'blocking');
        } catch (err) {
            toasted.error(catchRequestError(err));
            return false;
        }
    };

    const clearErrors = () => {
        bodyErrors.value = [];
    };

    return {
        bodyErrors,
        validate,
        clearErrors,
    };
};
